/**
 * An explicit list of accounts to narrow a report to.
 *
 * Two sources, because two reports mean two different populations and offering
 * the wrong one produces rows that can never appear:
 *
 *  - `settled`  — exactly the members who settled something in this range and
 *                 scope, read from the same `useMemberPnl` the P&L screens
 *                 already draw. Anyone else in the downline would be a name you
 *                 can tick and then watch return nothing, forever.
 *  - `downline` — every account beneath you, for Credit transfers, where the
 *                 counterparty can be any tier and has nothing to do with
 *                 whether they bet.
 *
 * The option list is fetched only while the popover is OPEN. On Transfers that
 * saves a whole-subtree read on every page view; on the P&L screens the query
 * is already in cache, so opening it costs nothing at all.
 *
 * Selection commits live — no Apply button. Every other control in this bar
 * commits live, and a single control that waits is the one an operator forgets
 * to press.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Checkbox, Icon, Popover, SearchInput, Spinner } from '@w7/ui'
import { hasLiveApi } from '@/api/client'
import { useSearchHierarchyNew } from '@/api/hooks/member/useSearchHierarchyNew'
import { useDownline, useMemberPnl } from '@/app/queries'
import { AccountCell } from '@/app/AppShell'
import { count } from '@/lib/format'
import { ROLES, isAgent, type Account, type DateRange, type Role } from '@/data/types'

export type MemberSource = 'settled' | 'downline' | 'hierarchy'

/** One page big enough for any subtree this console renders. */
const DOWNLINE_QUERY_SIZE = 2000

/** Deliberately the same shape Member P&L already asks for, so the two share a
 *  cache entry instead of each paying for the agent index. */
const AGENT_QUERY = { roles: ROLES.filter(isAgent) as Role[], pageSize: 200 }

export interface MemberPickerProps {
  selected: string[]
  onChange: (next: string[]) => void
  source: MemberSource
  range: DateRange
  /** The hierarchy scope. The picker offers only accounts inside it. */
  rootId: string | null
  /** Trigger caption, e.g. "All members" / "All counterparties". */
  allLabel?: string
  label?: string
}

export function MemberPicker({
  selected,
  onChange,
  source,
  range,
  rootId,
  allLabel = 'All members',
  label = 'Members',
}: MemberPickerProps) {
  const anchor = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  const caption = selected.length === 0 ? allLabel : `${count(selected.length)} selected`

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="w7-rangetrigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${caption}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="users" size={16} />
        <span className="w7-rangetrigger-text">
          <span className="w7-rangetrigger-preset">{label}</span>
          <span className="w7-rangetrigger-sub">{caption}</span>
        </span>
        <Icon name="chevron-down" size={14} />
      </button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        label={label}
        width={380}
      >
        {source === 'hierarchy' ? (
          <HierarchyOptions
            selected={selected}
            onChange={onChange}
            range={range}
            rootId={rootId}
            open={open}
          />
        ) : source === 'settled' ? (
          <SettledOptions selected={selected} onChange={onChange} range={range} rootId={rootId} />
        ) : (
          <DownlineOptions selected={selected} onChange={onChange} rootId={rootId} />
        )}
      </Popover>
    </>
  )
}

/* ------------------------------------------------------------------ panels -- */

interface Option {
  account: Account
  /** The parent agent's username — the disambiguator, never the subject. */
  parent: string | null
}

function SettledOptions({
  selected,
  onChange,
  range,
  rootId,
}: {
  selected: string[]
  onChange: (next: string[]) => void
  range: DateRange
  rootId: string | null
}) {
  const members = useMemberPnl({ range, ...(rootId ? { rootId } : {}) })
  /* Members sit two or three rungs below the caller, so naming their agent
     needs the subtree index. */
  const agents = useDownline(AGENT_QUERY)

  const options = useMemo<Option[]>(() => {
    const byId = new Map<string, Account>()
    for (const account of agents.data?.rows ?? []) byId.set(account.id, account)
    return (members.data?.rows ?? []).map((row) => ({
      account: row.account,
      parent: row.account.parentId ? (byId.get(row.account.parentId)?.username ?? null) : null,
    }))
  }, [agents.data, members.data])

  return (
    <OptionList
      options={options}
      loading={members.isLoading}
      selected={selected}
      onChange={onChange}
      emptyMessage="No member settled anything in this range, so there is nobody to filter to."
      searchLabel="Filter members"
    />
  )
}

function DownlineOptions({
  selected,
  onChange,
  rootId,
}: {
  selected: string[]
  onChange: (next: string[]) => void
  rootId: string | null
}) {
  const downline = useDownline({
    ...(rootId ? { rootId } : {}),
    directOnly: false,
    pageSize: DOWNLINE_QUERY_SIZE,
  })

  const options = useMemo<Option[]>(() => {
    const rows = downline.data?.rows ?? []
    const byId = new Map(rows.map((a) => [a.id, a]))
    return rows.map((account) => ({
      account,
      parent: account.parentId ? (byId.get(account.parentId)?.username ?? null) : null,
    }))
  }, [downline.data])

  return (
    <OptionList
      options={options}
      loading={downline.isLoading}
      selected={selected}
      onChange={onChange}
      emptyMessage="You hold no accounts beneath you yet."
      searchLabel="Filter counterparties"
    />
  )
}

/**
 * Lab PL Statement `ui-select multiple` backed by
 * `POST /v1/member/searchHierarchyNew`.
 *
 * Values are usernames, not account ids: Lab sends them unchanged as
 * `playerName`, and Agent-API accepts either usernames or UUIDs there.
 */
function HierarchyOptions({
  selected,
  onChange,
  range,
  rootId,
  open,
}: {
  selected: string[]
  onChange: (next: string[]) => void
  range: DateRange
  rootId: string | null
  open: boolean
}) {
  const [needle, setNeedle] = useState('')
  const [debouncedNeedle, setDebouncedNeedle] = useState('')
  const live = hasLiveApi()
  const hierarchy = useSearchHierarchyNew(debouncedNeedle, open && live, {
    prefetchOnEmpty: true,
  })
  const fixture = useMemberPnl({ range, ...(rootId ? { rootId } : {}) })

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedNeedle(needle), 100)
    return () => window.clearTimeout(timer)
  }, [needle])

  const suggestions = useMemo(() => {
    const rows = live
      ? (hierarchy.data ?? [])
      : (fixture.data?.rows ?? []).map((row) => row.account.username)
    const text = needle.trim().toLowerCase()
    const matching = live
      ? rows
      : rows.filter((username) => !text || username.toLowerCase().includes(text))
    return [...new Set([...selected, ...matching])]
  }, [fixture.data?.rows, hierarchy.data, live, needle, selected])

  const toggle = (username: string) =>
    onChange(
      selected.includes(username)
        ? selected.filter((item) => item !== username)
        : [...selected, username],
    )

  const loading = live ? hierarchy.isFetching : fixture.isLoading

  return (
    <>
      <SearchInput
        value={needle}
        onChange={setNeedle}
        label="Search hierarchy members"
        placeholder="Enter or select member name"
      />

      {loading && suggestions.length === selected.length ? (
        <span className="w7-row" style={{ gap: 'var(--w7-s2)' }}>
          <Spinner size={14} />
          <span className="w7-dim" style={{ fontSize: 12 }}>
            Searching downline
          </span>
        </span>
      ) : hierarchy.isError && live ? (
        <p className="w7-scopenote">Could not load members from hierarchy.</p>
      ) : suggestions.length === 0 ? (
        <p className="w7-scopenote">
          {needle.trim() ? `No members match “${needle.trim()}”.` : 'No members found beneath you.'}
        </p>
      ) : (
        <div className="w7-pickerlist">
          {suggestions.map((username) => (
            <div key={username} className="w7-pickerrow">
              <Checkbox
                checked={selected.includes(username)}
                onChange={() => toggle(username)}
                label={`Filter to ${username}`}
              />
              <span className="w7-pickerrow-body">{username}</span>
            </div>
          ))}
        </div>
      )}

      <div className="w7-pickerfoot">
        <span>{count(selected.length)} selected</span>
        <span className="w7-spacer" />
        <Button
          variant="ghost"
          size="sm"
          icon="close"
          onClick={() => onChange([])}
          disabled={selected.length === 0}
        >
          Clear members
        </Button>
      </div>
    </>
  )
}

/* -------------------------------------------------------------------- list -- */

function OptionList({
  options,
  loading,
  selected,
  onChange,
  emptyMessage,
  searchLabel,
}: {
  options: Option[]
  loading: boolean
  selected: string[]
  onChange: (next: string[]) => void
  emptyMessage: string
  searchLabel: string
}) {
  const [needle, setNeedle] = useState('')

  const shown = useMemo(() => {
    const text = needle.trim().toLowerCase()
    if (!text) return options
    return options.filter(
      (o) =>
        o.account.username.toLowerCase().includes(text) ||
        o.account.displayName.toLowerCase().includes(text),
    )
  }, [needle, options])

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id])

  return (
    <>
      <SearchInput value={needle} onChange={setNeedle} label={searchLabel} placeholder={searchLabel} />

      {loading ? (
        <span className="w7-row" style={{ gap: 'var(--w7-s2)' }}>
          <Spinner size={14} />
          <span className="w7-dim" style={{ fontSize: 12 }}>
            Loading
          </span>
        </span>
      ) : options.length === 0 ? (
        <p className="w7-scopenote">{emptyMessage}</p>
      ) : (
        <div className="w7-pickerlist">
          {shown.map((option) => (
            <div key={option.account.id} className="w7-pickerrow">
              <Checkbox
                checked={selected.includes(option.account.id)}
                onChange={() => toggle(option.account.id)}
                label={`Filter to ${option.account.username}`}
              />
              <span className="w7-pickerrow-body">
                <AccountCell
                  username={option.account.username}
                  displayName={option.account.displayName}
                  role={option.account.role}
                  /* The tier is already on the chip beside the username, so the
                     second line carries the one thing that actually
                     disambiguates two similar names: who they sit under.
                     Undefined falls back to the display name. */
                  meta={option.parent ? `under ${option.parent}` : undefined}
                />
              </span>
            </div>
          ))}
          {shown.length === 0 ? (
            <p className="w7-scopenote">Nothing here matches “{needle.trim()}”.</p>
          ) : null}
        </div>
      )}

      <div className="w7-pickerfoot">
        <span>{count(selected.length)} selected</span>
        <span className="w7-spacer" />
        <Button
          variant="ghost"
          size="sm"
          icon="close"
          onClick={() => onChange([])}
          disabled={selected.length === 0}
        >
          Clear
        </Button>
      </div>
    </>
  )
}
